import useMapbox, { zoomToEntities } from '@/composables/useMapbox'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useCoreEntityStore } from './coreEntity'
import { CoreEntityDto, CoreType } from '@/interfaces/core.interface'
import { ClickedEntityType } from './selection'

const ZOOM_THRESHOLD = 6
const PITCH_THRESHOLD = 60

const removeDuplicates = (arr: string[]): string[] => Array.from(new Set(arr)).filter(Boolean)
const removeDuplicatesByKey = <T>(arr: T[], keyGetter: (x: T) => string): T[] =>
  Array.from(new Map(arr.map((x) => [keyGetter(x), x])).values())
const zip = <T, U>(a: T[], b: U[]): [T, U][] => a.map((x, i) => [x, b[i]])
const transposeMax = <T>(arr: T[][]): T[][] => {
  const max = Math.max(...arr.map((x) => x.length))
  return Array.from({ length: max }, (_, i) => arr.map((x) => x[i]).filter((x) => x !== undefined))
}

const entityOrder: CoreType[] = ['COMPLEX', 'REAL_ESTATE', 'CONSTRUCTION', 'LEVEL', 'SPACE']
const entityOrderIndex = (type: CoreType) => entityOrder.indexOf(type)
const entityOrderCompare = (a: CoreEntityDto, b: CoreEntityDto) =>
  entityOrderIndex(a.type) - entityOrderIndex(b.type)

export const useSelectionStore = defineStore('selection-new', () => {
  const { map } = useMapbox()
  // TODO: replace with some new entity relation store that communicates with the new entity relation service in backend
  const { getCoreEntity, getParents } = useCoreEntityStore()

  // TODO: enable all once platform is more ready for multi select
  const multiSelectEnabled = ref<Partial<Record<ClickedEntityType, boolean>>>({
    CONSTRUCTION: true
  })

  const selectedEntityIds = ref<string[]>([])

  const selectedEntities = ref<CoreEntityDto[]>([])
  const entityPath = ref<CoreEntityDto[][]>([])
  const entityIdPath = computed(() => entityPath.value?.map((x) => x.map((x) => x.id)))
  const entityIdPathString = computed(() => entityIdPath.value.map((x) => x.join('|')).join('.'))
  const entitiesInPathByType = computed(() =>
    entityPath.value.flat().reduce((acc, x) => {
      acc[x.type] = [...(acc[x.type] ?? []), x]
      return acc
    }, {} as Record<CoreType, CoreEntityDto[]>)
  )
  const selectedEntitiesByType = computed(() =>
    selectedEntities.value.reduce((acc, x) => {
      acc[x.type] = [...(acc[x.type] ?? []), x]
      return acc
    }, {} as Record<CoreType, CoreEntityDto[]>)
  )
  const selectedEntityType = computed<CoreType | undefined>(() => {
    const last = selectedEntities.value.slice(-1)[0]
    return last?.type
  })

  map.on('click', (e) => {
    const feature = map.queryRenderedFeatures(e.point).at(0)

    if (feature?.properties?.id || feature?.properties?.belongsToId) {
      const id = (feature.properties.id ?? feature.properties.belongsToId) as string
      const type = feature.properties.type as ClickedEntityType
      if (e.originalEvent.ctrlKey && multiSelectEnabled.value[type]) {
        toggle([id])
      } else {
        set([id])
      }
    } else if (selectedEntityType.value === 'SPACE') {
      clear({ upTo: -2 })
    } else {
      clear()
    }
  })

  watch(selectedEntityIds, async (ids) => {
    if (ids.length > 0) {
      const entities = await Promise.all(ids.map((id) => getCoreEntity(id)))
      const parents = await Promise.all(
        ids.map(async (id) => (await getParents(id)).toSorted(entityOrderCompare))
      )
      const paths = zip(entities, parents).map(([entity, parents]) => [...parents, entity])
      entityPath.value = transposeMax(paths).map((x) => removeDuplicatesByKey(x, (x) => x.id))
      selectedEntities.value = entities
    } else {
      entityPath.value = []
      selectedEntities.value = []
    }
  })

  function set(ids: string[], zoom: boolean = false, zoomEntities?: string[]) {
    const newIds = removeDuplicates(ids)
    selectedEntityIds.value = newIds
    if (zoom) {
      zoomToEntities(zoomEntities ?? newIds, true, {
        zoomTreshold: ZOOM_THRESHOLD,
        pitchTreshold: PITCH_THRESHOLD,
        zoomTrigger: 'intersect'
      })
    }
  }

  function add(ids: string[]) {
    set([...selectedEntityIds.value, ...ids])
  }

  function remove(ids: string[]) {
    set(selectedEntityIds.value.filter((id) => !ids.includes(id)))
  }

  function toggle(ids: string[]) {
    const newIds = ids.filter((id) => !selectedEntityIds.value.includes(id))
    const removeIds = ids.filter((id) => selectedEntityIds.value.includes(id))
    set([...selectedEntityIds.value, ...newIds].filter((id) => !removeIds.includes(id)))
  }

  function clear({ upTo = 0 }: { upTo?: number } = {}) {
    let entityId: string | undefined
    if (upTo !== 0) {
      entityId = entityPath.value.at(upTo > 0 ? upTo - 1 : upTo)?.at(0)?.id
    } else {
      entityId = undefined
    }
    set(entityId ? [entityId] : [])
  }

  return {
    selectedEntityIds,
    selectedEntities,
    selectedEntitiesByType,
    entityPath,
    entityIdPath,
    entityIdPathString,
    entitiesInPathByType,
    selectedEntityType,
    set,
    add,
    remove,
    toggle,
    clear
  }
})
